import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  Save,
  CheckCircle,
  XCircle,
  Calendar,
  CreditCard,
  FileText
} from 'lucide-react';
import { companyDatabase } from '../utils/mainAppDatabase';
import { recordManualPayment } from '../services/paymentService';
import { backofficeAuth } from '../backofficeFirebaseClient';
import { loadSubscriptionPlans } from '../utils/subscriptionUtils';

const PaymentRecorder = () => {
  const [companies, setCompanies] = useState([]);
  const [usagePlans, setUsagePlans] = useState(null);
  const [plansError, setPlansError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Form state
  const [selectedCompany, setSelectedCompany] = useState('');
  const [paymentType, setPaymentType] = useState('plan');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('manual');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [topupName, setTopupName] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await loadSubscriptionPlans();
        if (cancelled) return;
        if (!data || Object.keys(data).length === 0) {
          setPlansError('Plan catalog is empty. Configure Plan Catalog before recording plan payments.');
          setUsagePlans({});
        } else {
          setUsagePlans(data);
          setPlansError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setPlansError(e.message || 'Failed to load plan catalog');
          setUsagePlans({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (paymentType === 'plan' && selectedPlan && usagePlans?.[selectedPlan]) {
      setAmount(String(usagePlans[selectedPlan].price));
    }
  }, [paymentType, selectedPlan, usagePlans]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const companiesData = await companyDatabase.getAllCompanies();
      setCompanies(companiesData);
    } catch (error) {
      setMessage({ type: 'error', text: `Error loading companies: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCompany) {
      setMessage({ type: 'error', text: 'Please select a company' });
      return;
    }

    if (paymentType !== 'plan' && (!amount || parseFloat(amount) <= 0)) {
      setMessage({ type: 'error', text: 'Please enter a valid amount' });
      return;
    }

    if (paymentType === 'plan' && (!usagePlans || !usagePlans[selectedPlan])) {
      setMessage({ type: 'error', text: plansError || 'Plan catalog not loaded' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const currentUser = backofficeAuth.currentUser;
      if (!currentUser) {
        throw new Error('Admin authentication required');
      }

      const paymentData = {
        amount:
          paymentType === 'plan' && usagePlans?.[selectedPlan]
            ? Number(usagePlans[selectedPlan].price)
            : parseFloat(amount),
        type: paymentType,
        method: paymentMethod,
        reference,
        notes,
        date: new Date(paymentDate).toISOString(),
        recordedBy: {
          adminId: currentUser.uid,
          adminEmail: currentUser.email,
          adminName: currentUser.displayName || currentUser.email
        }
      };

      // Add type-specific data
      if (paymentType === 'plan') {
        paymentData.planId = selectedPlan;
      } else if (paymentType === 'topup') {
        paymentData.topupName = topupName;
      }

      await recordManualPayment(selectedCompany, paymentData);

      setMessage({
        type: 'success',
        text: `Payment of ₹${amount} recorded successfully!`
      });

      // Reset form
      resetForm();

    } catch (error) {
      console.error('Error recording payment:', error);
      setMessage({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedCompany('');
    setPaymentType('plan');
    setAmount('');
    setPaymentMethod('manual');
    setReference('');
    setNotes('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setSelectedPlan('starter');
    setTopupName('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Payment Recorder</h1>
          </div>
          <p className="text-gray-600">Record manual payments for subscriptions and top-ups</p>
        </div>

        {plansError && (
          <div className="mb-4 p-4 rounded-lg bg-amber-50 text-amber-900 border border-amber-200 text-sm">
            {plansError}
          </div>
        )}

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Payment Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company *
              </label>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a company...</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name} ({company.id})
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Type *
              </label>
              <select
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="plan">Plan Subscription</option>
                <option value="topup">Top-up Purchase</option>
                <option value="upgrade">Plan Upgrade</option>
              </select>
            </div>

            {/* Plan Selection (if type is plan or upgrade) */}
            {(paymentType === 'plan' || paymentType === 'upgrade') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan *
                </label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {usagePlans && Object.keys(usagePlans).length > 0 ? (
                    Object.values(usagePlans).map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} (₹{plan.price})
                      </option>
                    ))
                  ) : (
                    <option value="">Loading plans…</option>
                  )}
                </select>
              </div>
            )}

            {/* Top-up Name (if type is topup) */}
            {paymentType === 'topup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Top-up Name *
                </label>
                <input
                  type="text"
                  value={topupName}
                  onChange={(e) => setTopupName(e.target.value)}
                  required
                  placeholder="e.g., Operations Mega Pack"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (₹){paymentType === 'plan' ? ' (from catalog)' : ' *'}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                  ₹
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required={paymentType !== 'plan'}
                  readOnly={paymentType === 'plan'}
                  disabled={paymentType === 'plan'}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method *
                </label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                  >
                    <option value="manual">Manual</option>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="upi">UPI</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Date *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Reference
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Transaction ID, Receipt No., Cheque No., etc."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Add any additional information about this payment..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {loading ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Important Notes:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>All payments are recorded in the Firebase payment_history collection</li>
                <li>Manual payments require proper documentation and reference</li>
                <li>Payment records are immutable once created</li>
                <li>Ensure all details are correct before submitting</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRecorder;



















































